import { Box, Flex, HStack, VStack, useMediaQuery, Text, Pressable, Avatar } from 'native-base';
import { useState, useEffect, useCallback, useRef } from 'react';
import { MOBILE_VIEW_WIDTH } from '../../../../utils/screens';
import CustomModal from '../../../../components/modal';
import CustomButton from '../../../../components/button';
import InputField from '../../../../components/input';
import TextArea from '../../../../components/textArea';
import Dropdown from '../../../../components/dropdown';
import { Toaster } from '../../../../components/toaster';
import { COLORS } from '../../../../utils/colors';
import { deleteCardsIconSVG } from '../../../../utils/svg';
import { Svg } from 'react-native-svg';

const QuickMeetModal = ({ isOpen, onClose, onSubmit, editData = null }) => {
  const [MobileWindow] = useMediaQuery({ maxWidth: MOBILE_VIEW_WIDTH });
  const previousEditData = useRef(editData);
  const isAddMode = !editData;
  const toaster = Toaster();

  // Get initial state
  const getInitialState = useCallback(
    () => ({
      hotel_name: '',
      city: '',
      hotel_chain: '',
      hotel_spoc: '',
      spoc_number: '',
      spoc_email: '',
      banquet_venue: '',
      location: '',
      cluster: '',
      theatre: '',
      head_count: [{ head_count: '', rate: '' }], // Default one entry
      remarks: '',
      dishes: {
        "Soups": '',
        "Starters": '',
        "Salads": '',
        "Main course": '',
        "Live Counters": '',
        "Sides": '',
        "Beverages": '',
        "Desserts": ''
      },
      errors: {},
      showErrors: false
    }),
    []
  );

  // Initialize state
  const [hotelData, setHotelData] = useState({
    hotel_name: '',
    city: '',
    hotel_chain: '',
    hotel_spoc: '',
    spoc_number: '',
    spoc_email: '',
    banquet_venue: '',
    location: '',
    cluster: '',
    theatre: '',
    head_count: [{ head_count: '', rate: '' }], // Default one entry
    remarks: '',
    dishes: {
      "Soups": '',
      "Starters": '',
      "Salads": '',
      "Main course": '',
      "Live Counters": '',
      "Sides": '',
      "Beverages": '',
      "Desserts": ''
    }
  });
  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Force reset function that definitely clears everything
  const forceReset = useCallback(() => {
    const initialState = getInitialState();
    setHotelData({
      hotel_name: initialState.hotel_name,
      city: initialState.city,
      hotel_chain: initialState.hotel_chain,
      hotel_spoc: initialState.hotel_spoc,
      spoc_number: initialState.spoc_number,
      spoc_email: initialState.spoc_email,
      banquet_venue: initialState.banquet_venue,
      location: initialState.location,
      cluster: initialState.cluster,
      theatre: initialState.theatre,
      head_count: initialState.head_count,
      remarks: initialState.remarks,
      dishes: initialState.dishes
    });
    setErrors({});
    setShowErrors(false);
    setIsInitialized(false);
    setIsSubmitting(false);
  }, [getInitialState]);

  // Function to populate form with edit data
  const populateEditData = useCallback(() => {
    if (!editData) return;

    // Convert dishes values to strings if they exist
    const convertDishesToStrings = (dishes) => {
      if (!dishes) return {
        "Soups": '',
        "Starters": '',
        "Salads": '',
        "Main course": '',
        "Live Counters": '',
        "Sides": '',
        "Beverages": '',
        "Desserts": ''
      };
      
      return Object.fromEntries(
        Object.entries(dishes).map(([key, value]) => [
          key, 
          value === 0 ? '' : value?.toString() || ''
        ])
      );
    };

    setHotelData({
      hotel_name: editData.hotel_name || '',
      city: editData.city || '',
      hotel_chain: editData.hotel_chain || '',
      hotel_spoc: editData.hotel_spoc || '',
      spoc_number: editData.spoc_number?.toString() || '',
      spoc_email: editData.spoc_email || '',
      banquet_venue: editData.banquet_venue || '',
      location: editData.location || '',
      cluster: editData.cluster?.toString() || '',
      theatre: editData.theatre?.toString() || '',
      head_count: editData.head_count && editData.head_count.length > 0 ? editData.head_count : [{ head_count: '', rate: '' }],
      remarks: editData.remarks || '',
      dishes: convertDishesToStrings(editData.dishes)
    });
    setErrors({});
    setShowErrors(false);
    setIsSubmitting(false);
  }, [editData]);

  // Main effect to handle modal opening
  useEffect(() => {
    if (isOpen) {
      setIsInitialized(false);

      if (isAddMode) {
        // Add mode - always start fresh
        forceReset();
        setTimeout(() => {
          setIsInitialized(true);
        }, 50);
      } else {
        // Edit mode - populate with data
        populateEditData();
        setTimeout(() => {
          setIsInitialized(true);
        }, 50);
      }
    }
  }, [isOpen, isAddMode, forceReset, populateEditData]);

  // Handle mode changes (switching between add and edit)
  useEffect(() => {
    const hasEditDataChanged = previousEditData.current !== editData;
    previousEditData.current = editData;

    if (hasEditDataChanged && isOpen) {
      setIsInitialized(false);

      if (isAddMode) {
        forceReset();
      } else {
        populateEditData();
      }

      setTimeout(() => {
        setIsInitialized(true);
      }, 50);
    }
  }, [editData, isOpen, isAddMode, forceReset, populateEditData]);

  // Reset when modal closes (for add mode)
  useEffect(() => {
    if (!isOpen && isAddMode) {
      const timeoutId = setTimeout(() => {
        forceReset();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, isAddMode, forceReset]);

  // Enhanced onClose handler
  const handleClose = useCallback(() => {
    // Immediately reset if add mode
    if (isAddMode) {
      forceReset();
    }

    onClose();
  }, [isAddMode, onClose, forceReset]);

  const updateField = (field, value) => {
    setHotelData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const updateDishField = (dishType, value) => {
    setHotelData((prev) => ({
      ...prev,
      dishes: {
        ...prev.dishes,
        [dishType]: value
      }
    }));
  };

  // Head count management functions
  const addHeadCountEntry = () => {
    setHotelData((prev) => ({
      ...prev,
      head_count: [
        ...prev.head_count,
        { head_count: '', rate: '' }
      ]
    }));
  };

  const removeHeadCountEntry = (index) => {
    setHotelData((prev) => ({
      ...prev,
      head_count: prev.head_count.filter((_, i) => i !== index)
    }));
  };

  const updateHeadCountEntry = (index, field, value) => {
    setHotelData((prev) => ({
      ...prev,
      head_count: prev.head_count.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Hotel name validation (required)
    if (!hotelData.hotel_name.trim()) {
      newErrors.hotel_name = 'Hotel name is required';
      isValid = false;
    } else if (hotelData.hotel_name.length < 3) {
      newErrors.hotel_name = 'Hotel name must be at least 3 characters';
      isValid = false;
    }

    // City validation (required)
    if (!hotelData.city.trim()) {
      newErrors.city = 'City is required';
      isValid = false;
    }

    // Hotel chain validation (required)
    if (!hotelData.hotel_chain.trim()) {
      newErrors.hotel_chain = 'Hotel chain is required';
      isValid = false;
    }

    // Hotel SPOC validation (required)
    if (!hotelData.hotel_spoc.trim()) {
      newErrors.hotel_spoc = 'Hotel SPOC is required';
      isValid = false;
    }

    // SPOC number validation (required, valid format)
    const spocNumber = String(hotelData.spoc_number || '');
    if (!spocNumber.trim()) {
      newErrors.spoc_number = 'SPOC number is required';
      isValid = false;
    } else if (!/^[\+]?[0-9\-\s\(\)]{10,15}$/.test(spocNumber.trim())) {
      newErrors.spoc_number = 'Please enter a valid phone number (10-15 digits)';
      isValid = false;
    }

    // SPOC email validation (required, valid format)
    if (!hotelData.spoc_email.trim()) {
      newErrors.spoc_email = 'SPOC email is required';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(hotelData.spoc_email.trim())) {
      newErrors.spoc_email = 'Please enter a valid email address';
      isValid = false;
    }

    // Banquet venue validation (required)
    if (!hotelData.banquet_venue.trim()) {
      newErrors.banquet_venue = 'Banquet venue is required';
      isValid = false;
    }

    // Cluster validation (required, positive number)
    if (!hotelData.cluster.trim()) {
      newErrors.cluster = 'Cluster is required';
      isValid = false;
    } else if (
      !/^\d+(\.\d+)?$/.test(hotelData.cluster) ||
      parseFloat(hotelData.cluster) <= 0
    ) {
      newErrors.cluster = 'Cluster must be a positive number';
      isValid = false;
    }

    // Theatre validation (required, positive number)
    if (!hotelData.theatre.trim()) {
      newErrors.theatre = 'Theatre is required';
      isValid = false;
    } else if (
      !/^\d+(\.\d+)?$/.test(hotelData.theatre) ||
      parseFloat(hotelData.theatre) <= 0
    ) {
      newErrors.theatre = 'Theatre must be a positive number';
      isValid = false;
    }

    // Head count validation (at least one complete entry required)
    if (!hotelData.head_count || hotelData.head_count.length === 0) {
      newErrors.head_count = 'At least one head count entry is required';
      isValid = false;
    } else {
      // Check if at least one entry is complete
      const hasCompleteEntry = hotelData.head_count.some(entry => 
        entry.head_count.trim() && entry.rate.trim()
      );
      
      if (!hasCompleteEntry) {
        newErrors.head_count = 'At least one head count and rate entry is required';
        isValid = false;
      }
      
      // Validate each head count entry
      hotelData.head_count.forEach((entry, index) => {
        if (entry.head_count.trim() && !entry.rate.trim()) {
          newErrors[`rate_${index}`] = 'Rate is required when head count is provided';
          isValid = false;
        }
        if (!entry.head_count.trim() && entry.rate.trim()) {
          newErrors[`head_count_${index}`] = 'Head count is required when rate is provided';
          isValid = false;
        }
      });
    }

    // Remarks validation (required)
    if (!hotelData.remarks.trim()) {
      newErrors.remarks = 'Remarks are required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleHotelSubmit = async () => {
    const isValid = validateForm();
    setShowErrors(true);

    if (isValid) {
      setIsSubmitting(true);
      
      // Keep cluster and theatre as strings, head_count as array, dishes are already strings
      // Filter out empty head_count entries
      const filteredHeadCount = hotelData.head_count.filter(entry => 
        entry.head_count.trim() && entry.rate.trim()
      );
      
      const submissionData = {
        ...hotelData,
        spoc_number: String(hotelData.spoc_number || ''),
        cluster: hotelData.cluster,
        theatre: hotelData.theatre,
        head_count: filteredHeadCount,
        dishes: Object.fromEntries(
          Object.entries(hotelData.dishes).map(([key, value]) => [
            key, 
            value === '' ? 0 : parseInt(value) || 0
          ])
        )
      };

      try {
        await onSubmit(submissionData);
        
        // Only reset and close if submission was successful
        if (isAddMode) {
          forceReset();
        }
        onClose();
      } catch (error) {
        // Handle API error - don't close modal, show error message
        const errorMessage = error?.message || 'An error occurred while submitting the hotel data';
        toaster(errorMessage, 'error', 'error');
        setIsSubmitting(false);
      }
    } else {
      // Get the first error message to show in toaster
      const firstError = Object.values(errors)[0];
      toaster(
        firstError || 'Please fill in all required fields correctly',
        'error',
        'error'
      );
    }
  };

  const modalBody = () => (
    <Box>
      <VStack space={4}>
        {/* Hotel Name */}
        <InputField
          label='Hotel Name *'
          value={hotelData.hotel_name}
          onChange={(e) => updateField('hotel_name', e.target.value)}
          isInvalid={showErrors && !!errors.hotel_name}
          errorMessage={showErrors ? errors.hotel_name : ''}
        />

        {/* City and Hotel Chain Row */}
        <HStack space={4}>
          <Box flex={1}>
            <InputField
              label='City *'
              value={hotelData.city}
              onChange={(e) => updateField('city', e.target.value)}
              isInvalid={showErrors && !!errors.city}
              errorMessage={showErrors ? errors.city : ''}
            />
          </Box>
          <Box flex={1}>
            <InputField
              label='Hotel Chain *'
              value={hotelData.hotel_chain}
              onChange={(e) => updateField('hotel_chain', e.target.value)}
              isInvalid={showErrors && !!errors.hotel_chain}
              errorMessage={showErrors ? errors.hotel_chain : ''}
            />
          </Box>
        </HStack>

        {/* Hotel SPOC and SPOC Number Row */}
        <HStack space={4}>
          <Box flex={1}>
            <InputField
              label='Hotel SPOC *'
              value={hotelData.hotel_spoc}
              onChange={(e) => updateField('hotel_spoc', e.target.value)}
              isInvalid={showErrors && !!errors.hotel_spoc}
              errorMessage={showErrors ? errors.hotel_spoc : ''}
            />
          </Box>
          <Box flex={1}>
            <InputField
              label='SPOC Number *'
              value={hotelData.spoc_number}
              onChange={(e) => updateField('spoc_number', e.target.value)}
              keyboardType='phone-pad'
              isInvalid={showErrors && !!errors.spoc_number}
              errorMessage={showErrors ? errors.spoc_number : ''}
            />
          </Box>
        </HStack>

        {/* SPOC Email */}
        <InputField
          label='SPOC Email *'
          value={hotelData.spoc_email}
          onChange={(e) => updateField('spoc_email', e.target.value)}
          keyboardType='email-address'
          isInvalid={showErrors && !!errors.spoc_email}
          errorMessage={showErrors ? errors.spoc_email : ''}
        />

        {/* Banquet Venue */}
        <InputField
          label='Banquet Venue *'
          value={hotelData.banquet_venue}
          onChange={(e) => updateField('banquet_venue', e.target.value)}
          isInvalid={showErrors && !!errors.banquet_venue}
          errorMessage={showErrors ? errors.banquet_venue : ''}
        />

        {/* Location */}
        <InputField
          label='Location'
          value={hotelData.location}
          onChange={(e) => updateField('location', e.target.value)}
          placeholder='Enter location or Google Maps link'
        />

        {/* Cluster and Theatre Row */}
        <HStack space={4}>
          <Box flex={1}>
            <InputField
              label='Cluster *'
              value={hotelData.cluster}
              onChange={(e) => updateField('cluster', e.target.value)}
              keyboardType='numeric'
              isInvalid={showErrors && !!errors.cluster}
              errorMessage={showErrors ? errors.cluster : ''}
            />
          </Box>
          <Box flex={1}>
            <InputField
              label='Theatre *'
              value={hotelData.theatre}
              onChange={(e) => updateField('theatre', e.target.value)}
              keyboardType='numeric'
              isInvalid={showErrors && !!errors.theatre}
              errorMessage={showErrors ? errors.theatre : ''}
            />
          </Box>
        </HStack>

        {/* Head Count and Rate Section */}
        <Box>
          <HStack justifyContent="space-between" alignItems="center" mb={3}>
            <Text fontSize="md" fontWeight="bold">Head Count & Rate *</Text>
            <CustomButton
              text="Add Entry"
              onPress={addHeadCountEntry}
              bgColor={COLORS.THEME_COLOR}
              textColor={COLORS.WHITE_COLOR}
              size="sm"
            />
          </HStack>
          
          {showErrors && errors.head_count && (
            <Text color="red.500" fontSize="sm" mb={2}>
              {errors.head_count}
            </Text>
          )}

          <VStack space={3}>
            {hotelData.head_count.map((entry, index) => (
              <Box
                key={index}
                p={3}
                borderWidth={1}
                borderColor={COLORS.BORDER_COLOR}
                borderRadius="md"
                bg={COLORS.WHITE_COLOR}
              >
                <HStack space={3} alignItems="flex-start">
                  <Box flex={1}>
                    <Dropdown
                      label="Head Count"
                      name={`head_count_${index}`}
                      selectedValues={entry.head_count}
                      setSelectedValues={(value) => updateHeadCountEntry(index, 'head_count', value)}
                      dropItems={[
                        { value: '25 - 50', label: '25 - 50' },
                        { value: '50 - 100', label: '50 - 100' },
                        { value: '100 - 200', label: '100 - 200' },
                        { value: 'Above 200', label: 'Above 200' }
                      ]}
                      placeholder="Select Head Count"
                      isInvalid={showErrors && !!errors[`head_count_${index}`]}
                      errorMessage={showErrors ? errors[`head_count_${index}`] : ''}
                    />
                  </Box>
                  <Box flex={1}>
                    <InputField
                      label="Rate (₹)"
                      value={entry.rate}
                      onChange={(e) => updateHeadCountEntry(index, 'rate', e.target.value)}
                      placeholder="e.g., 3000 Breakfast"
                      isInvalid={showErrors && !!errors[`rate_${index}`]}
                      errorMessage={showErrors ? errors[`rate_${index}`] : ''}
                    />
                  </Box>
                  {hotelData.head_count.length > 1 && (
                    <Box pt={6}>
                      <Pressable
                        onPress={() => removeHeadCountEntry(index)}
                        p={2}
                        borderRadius="md"
                        _pressed={{ bg: COLORS.BORDER_COLOR }}
                      >
                        <Avatar
                          bg={'none'}
                          size={'sm'}
                        >
                          <Svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            {deleteCardsIconSVG}
                          </Svg>
                        </Avatar>
                      </Pressable>
                    </Box>
                  )}
                </HStack>
              </Box>
            ))}
          </VStack>
        </Box>

        {/* Dishes Section */}
        <Box>
          <Text fontSize="md" fontWeight="bold" mb={3}>Dishes</Text>
          <VStack space={3}>
            {Object.keys(hotelData.dishes).map((dishType) => (
              <HStack key={dishType} space={3} alignItems="center">
                <Box flex={2}>
                  <Text fontSize="sm" fontWeight="medium">{dishType}</Text>
                </Box>
                <Box flex={1}>
                  <InputField
                    label=''
                    value={hotelData.dishes[dishType]}
                    onChange={(e) => updateDishField(dishType, e.target.value)}
                    keyboardType='numeric'
                    placeholder='e.g., 10'
                  />
                </Box>
              </HStack>
            ))}
          </VStack>
        </Box>
        {/* Remarks */}
        <TextArea
          label='Remarks *'
          value={hotelData.remarks}
          onChange={(e) => updateField('remarks', e.target.value)}
          placeholder='Additional remarks or special requirements...'
          isInvalid={showErrors && !!errors.remarks}
          errorMessage={showErrors ? errors.remarks : ''}
        />
      </VStack>
    </Box>
  );

  const modalFooter = () => (
    <Flex
      direction='row'
      justifyContent='flex-end'
      width='100%'
    >
      <HStack space={2}>
        <CustomButton
          onPress={handleClose}
          text='Cancel'
          variant='outline'
        />
        <CustomButton
          onPress={handleHotelSubmit}
          text={editData ? 'Update Hotel' : 'Add Hotel'}
          isLoading={isSubmitting}
          disabled={isSubmitting}
        />
      </HStack>
    </Flex>
  );

  // Don't render the modal content until it's properly initialized
  if (isOpen && !isInitialized) {
    return null;
  }

  return (
    <CustomModal
      key={
        isAddMode
          ? 'add-mode'
          : `edit-${editData?.id || editData?.hotel_name || 'unknown'}`
      }
      isOpen={isOpen}
      onClose={handleClose}
      header={`${editData ? 'Edit' : 'Add'} Hotel Details`}
      body={modalBody()}
      footer={modalFooter()}
      width={MobileWindow ? '100%' : '70%'}
      maxH={MobileWindow ? '90vh' : '80vh'}
    />
  );
};

export default QuickMeetModal;
